"use client"

import type { ElementRef, FC, ReactNode, SVGProps } from "react"
import { forwardRef, memo, useRef, useState } from "react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import {
	InternalAlertDialog,
	InternalAlertDialogAction,
	InternalAlertDialogCancel,
	InternalAlertDialogContent,
	InternalAlertDialogDescription,
	InternalAlertDialogFooter,
	InternalAlertDialogHeader,
	InternalAlertDialogTitle,
} from "./internal/alert-dialog"

export interface AlertDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	title: ReactNode
	description: ReactNode
	cancelText?: string
	confirmText: string
	onCancel: () => void | Promise<void>
	onConfirm: () => void | Promise<void>
	variant?: "positive" | "negative" | "outline"
	cancelIcon?: LucideIcon
	confirmIcon?: LucideIcon
	withCloseBadge?: boolean
	onClickCloseBadge?: () => void
	disabled?: boolean
	autoFocus?: "cancel" | "action"
}

export const AlertDialog = memo(
	forwardRef<ElementRef<typeof InternalAlertDialogContent>, AlertDialogProps>(
		(
			{
				title,
				description,
				confirmText,
				onCancel,
				onConfirm,
				cancelText = "Cancel",
				isOpen,
				onOpenChange,
				variant = "negative",
				cancelIcon,
				confirmIcon,
				withCloseBadge,
				onClickCloseBadge,
				disabled = false,
				autoFocus,
			},
			ref,
		) => {
			const confirmRef = useRef<HTMLButtonElement>(null)
			const [isConfirmLoading, setIsConfirmLoading] = useState(false)
			const [isCancelLoading, setIsCancelLoading] = useState(false)

			return (
				<InternalAlertDialog open={isOpen} onOpenChange={onOpenChange}>
					<InternalAlertDialogContent
						ref={ref}
						onCloseAutoFocus={(e) => {
							e.preventDefault()
							document.body.style.pointerEvents = ""
						}}
						onOpenAutoFocus={(e) => {
							e.preventDefault()
							confirmRef.current?.focus()
						}}
					>
						{withCloseBadge && (
							<button
								onClick={onClickCloseBadge ?? onCancel}
								className="absolute right-2 top-2"
							>
								<X className="h-4 w-4 text-primary" />
							</button>
						)}
						<InternalAlertDialogHeader>
							<InternalAlertDialogTitle>{title}</InternalAlertDialogTitle>
							<InternalAlertDialogDescription>
								{description}
							</InternalAlertDialogDescription>
						</InternalAlertDialogHeader>
						<InternalAlertDialogFooter>
							<InternalAlertDialogCancel
								onClick={async (e) => {
									e.preventDefault()
									setIsCancelLoading(true)
									try {
										await onCancel()
									} finally {
										setIsCancelLoading(false)
									}
									onOpenChange(false)
								}}
								className="w-full sm:w-auto"
								Icon={cancelIcon}
								isLoading={isCancelLoading}
								disabled={disabled}
								autoFocus={autoFocus === "cancel"}
							>
								{cancelText}
							</InternalAlertDialogCancel>
							<InternalAlertDialogAction
								disabled={disabled}
								variant={
									variant === "negative"
										? "danger"
										: variant === "outline"
											? "outline"
											: "default"
								}
								isLoading={isConfirmLoading}
								autoFocus={autoFocus === "action"}
								Icon={confirmIcon}
								onClick={async (e) => {
									e.preventDefault()
									setIsConfirmLoading(true)
									try {
										await onConfirm()
									} finally {
										setIsConfirmLoading(false)
									}
									onOpenChange(false)
								}}
								ref={confirmRef}
								className="w-full sm:w-auto"
							>
								{confirmText}
							</InternalAlertDialogAction>
						</InternalAlertDialogFooter>
					</InternalAlertDialogContent>
				</InternalAlertDialog>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
